import React, { useEffect, useState } from 'react';
import { PanelBar, PanelBarItem } from '@progress/kendo-react-layout';
import Avatar, { AvatarSize } from 'components/avatar/Avatar';
import CustomIcon from 'components/icon/CustomIcon';
import { icons } from 'utils/icons';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Loading from 'components/spinner/Loading';
import useInfoModal from 'utils/hooks/useInfoModal';
import { CONFERENCE_TYPE_VIDEO } from 'utils/constants';
import { useCreateConversationMutation, useCreateMeetingConversationMutation } from 'app/services/room.service';
import { useLazyCreateChannelConferenceQuery } from 'app/services/conference.service';
import { UserStatus } from 'types/user.types';
import { useTranslation } from 'react-i18next';
import { ERRORS, USER_STATUS } from 'locales/locales';
import { useAppSelector, useIsElectron } from 'app/hooks';
import { useCreateConference } from 'utils/hooks/useCreateConference';
import { FixMeLater } from 'types';

interface Group {
    name: string;
    imageUrl?: string;
    backgroundColor?: string;
    members?: Member[];
}

interface Member {
    userId: string;
    fullname: string;
    status?: UserStatus;
    imageUrl?: string;
}

interface GroupPanelProps {
    groups: Group[];
    userId: string;
    error?: string;
}

const memberStatusToTranslationKey = (status: UserStatus | string): string =>
  status === UserStatus.DONOTDISTURB || status === 'DONOTDISTURB'
    ? USER_STATUS.DO_NOT_DISTURB
    : String(status)

export const GroupPanel: React.FC<GroupPanelProps> = ({ groups, userId, error }) => {
    const navigate = useNavigate();


    const { t } = useTranslation();
    const [createConversation] = useCreateConversationMutation();
    const [createConference] = useLazyCreateChannelConferenceQuery();
    const { openModal: openConferenceModal, closeModal: closeConferenceModal, Modal: ConferenceModal } = useInfoModal();
    const myId = useAppSelector((state) => state.auth.id)
    const isElectron = useIsElectron()
    const [windowApi, setWindowApi] = useState<FixMeLater>(null)


    useEffect(() => {
        const w = window as FixMeLater
        if (w.api) setWindowApi(w.api)
      }, [])

      const [createMeetingConversation] = useCreateMeetingConversationMutation()


    const { handleCreateConference } = useCreateConference({
        createConference,
        createMeetingConversation,
        isElectron,
        windowApi,
        closeConferenceModal,
      });


    const onClickChatIcon = async (memberId: string): Promise<string | null> => {
        try {
            Loading.ref.show();
            const result = await createConversation({ members: [memberId] });
            if (!result.error) {
                const roomId = typeof result.data.data === 'string' ? result.data.data : result.data.data._id;
                navigate('/messages/' + roomId);
                return roomId;
            }
        } catch (error) {
            console.error('Error in creating or fetching conversation:', error);
        } finally {
            Loading.ref.hide();
        }
        return null;
    };

    const onVideoCall = async (memberId: string) => {
        openConferenceModal({
            title: t('Create Video Conference'),
            description: t('Would you like to start a video conference?'),
            cancelTitle: t('Cancel'),
            okTitle: t('Yes'),
            onCancel: closeConferenceModal,
            onOk: () => handleCreateConference(CONFERENCE_TYPE_VIDEO, [myId, memberId], null)
        });
    };



    if (error) {
        return <div>{t(ERRORS.DONT_HAVE_ACCESS)}</div>;
    }
    //console.log(groups)
    if (!groups.length) {
        return <div>{t(ERRORS.NO_CHANNELS_FOUND)}</div>;
    }


    return (
        <>
            <PanelBar>
                {groups.map((group,index:number) => (
                    <PanelBarItem
                        title={
                            <GroupTitle>
                                <Avatar text={group.name} source={group.imageUrl} size={AvatarSize.small} color={group.backgroundColor || 'orange'} />
                                <GroupName>{group.name}</GroupName>
                            </GroupTitle>
                        }
                        key={group.name + index}
                    >
                        {group?.members?.length && group?.members?.map((member, index:number) => (

                            <MemberInfo key={member.fullname+member.userId+ index}>
                                <MemberDetails>
                                    <Avatar source={member.imageUrl} size={AvatarSize.small} status={member.status} text={member.fullname} />
                                    <span style={{ marginLeft: '10px' }}>{member.fullname}</span>
                                    {member.status && (
                                        <span style={{ marginLeft: '5px', color: 'gray' }}>
                                            ({t(memberStatusToTranslationKey(member.status))})
                                        </span>
                                    )}
                                </MemberDetails> {member.userId !== myId && (
                                <IconsContainer>
                                    <CustomIcon
                                        width="18px"
                                        height="18px"
                                        source={icons.chatIconBlue}
                                        onClick={() => onClickChatIcon(member.userId)}
                                    />
                                    <CustomIcon
                                        width="18px"
                                        height="12px"
                                        source={icons.cameraIconBlue}
                                        onClick={() => onVideoCall(member.userId)}
                                    />
                                </IconsContainer>  )}
                            </MemberInfo>

                        ))}
                    </PanelBarItem>
                ))}
            </PanelBar>
            {ConferenceModal}
        </>
    );
};

const GroupTitle = styled.div`
    display: flex;
    align-items: center;
`;

const GroupName = styled.span`
    margin-left: 10px;
`;

const MemberInfo = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 5px;
`;

const MemberDetails = styled.div`
    display: flex;
    align-items: center;
`;

const IconsContainer = styled.div`
    display: flex;
    gap: 10px;
    align-items: center;
`;
